import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@aqx-core/common/api/auth';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { EDevicePlatform, useCompanyRegistrationUrl } from '@aqx-core/common/services/company';

import { useAuthRoutes } from './use-auth-routes';

interface IUseLoginSignUpAction {
  selectedTab: ELoginAs | undefined;
  /* Pass in the navigate function which navigates to sign up CRM screen/route */
  navigateSignUpCRM: () => void;
  /* Pass in the navigate function which navigates to sign up demo screen/route */
  navigateSignUpDemo: () => void;
  /* Pass in the open url function which accepts a url string  */
  openUrlCallback: (url: string) => void;
  /* Optional device platform for platform-specific registration URLs */
  devicePlatform?: EDevicePlatform;
}

export const useLoginSignUpAction = ({
  selectedTab,
  navigateSignUpCRM,
  navigateSignUpDemo,
  openUrlCallback,
  devicePlatform
}: IUseLoginSignUpAction) => {
  const { t } = useTranslation();
  const { hasCrmSignUpButton, hasCrmSignUpRoute, hasLiveSignUpButton, hasDemoSignUpButton } = useAuthRoutes();
  const registrationUrl = useCompanyRegistrationUrl(selectedTab, devicePlatform);

  const {
    text: signUpText,
    onClick: onClickSignUp,
    dataTestId
  } = useMemo(() => {
    const OPEN_DEMO = t('Open Demo Account');
    const SIGN_UP = t('Sign Up');

    // CRM
    if (selectedTab === ELoginAs.LIVE && hasCrmSignUpButton) {
      const onClick = () => (hasCrmSignUpRoute ? navigateSignUpCRM() : openUrlCallback(registrationUrl || ''));

      return { text: SIGN_UP, onClick, dataTestId: EDataTestId.LOGIN_ACCOUNT_SIGNUP };
    }
    // LIVE tabs (MT4_LIVE, MT5_LIVE, etc.) - only show button if registration URL is available
    if (selectedTab && LIVE_LOGINS_WITHOUT_CRM.includes(selectedTab) && hasLiveSignUpButton && registrationUrl) {
      const onClick = () => openUrlCallback(registrationUrl);

      return { text: SIGN_UP, onClick, dataTestId: EDataTestId.LOGIN_ACCOUNT_SIGNUP };
    }
    // DEMO tabs - always show button, use internal form if no URL
    if (selectedTab && DEMO_LOGINS.includes(selectedTab) && hasDemoSignUpButton) {
      const onClick = () => (registrationUrl ? openUrlCallback(registrationUrl) : navigateSignUpDemo());
      return { text: OPEN_DEMO, onClick };
    }
    return { text: '', onClick: () => {} };
  }, [
    t,
    selectedTab,
    hasCrmSignUpButton,
    hasLiveSignUpButton,
    hasDemoSignUpButton,
    hasCrmSignUpRoute,
    navigateSignUpCRM,
    openUrlCallback,
    registrationUrl,
    navigateSignUpDemo
  ]);

  return {
    onPress: onClickSignUp,
    signUpText,
    dataTestId
  };
};
